import { type ReactNode, useId } from 'react';
import { Dialog as AriaDialog } from 'react-aria-components';
import styled, { css } from 'styled-components';
import { UiLibModal } from '../../UiLibModal';
import { useTranslation } from '../../contexts/UIContext';
import {
  DESKTOP_INFO_MODAL_HORIZONTAL_PADDING_LG,
  DESKTOP_INFO_MODAL_HORIZONTAL_PADDING_SM,
  DESKTOP_INFO_MODAL_VERTICAL_GAP,
  DESKTOP_INFO_MODAL_VERTICAL_PADDING_LG,
  DESKTOP_INFO_MODAL_VERTICAL_PADDING_SM,
  GREY_300,
  MOBILE_INFO_MODAL_HORIZONTAL_PADDING_LG,
  MOBILE_INFO_MODAL_HORIZONTAL_PADDING_SM,
  MOBILE_INFO_MODAL_VERTICAL_GAP,
  MOBILE_INFO_MODAL_VERTICAL_PADDING_LG,
  MOBILE_INFO_MODAL_VERTICAL_PADDING_SM,
  PURPLE_1000,
  SIZING_3,
  SPACING_0_5,
  SPACING_3,
  SPACING_6,
} from '../../theme';
import { Badge } from '../Badge';
import { Button } from '../Button';
import Icons from '../Icons';
import { Stepper, type StepperProps } from '../Stepper';
import { Text, textStyles } from '../Text';
import { UiLibCloseButton } from '../UiLibCloseButton';
import media from '../utils/media';

type DataFields = Record<`data-${string}`, string | boolean>;

type ActionTarget =
  | {
      href: string;
      newTab?: boolean;
      onClick?: () => void;
    }
  | {
      href?: undefined;
      onClick: () => void;
    };

type Action = {
  text: string;
  trailingIcon?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
} & DataFields &
  ActionTarget;

type WithStepper = {
  steps: Required<Omit<StepperProps['steps'][number], 'href'>>[];
  activeIndex: StepperProps['activeIndex'];
  confirmAction: Action;
};

type Size = 'lg' | 'sm';

type SizeSm =
  | {
      size: 'sm';
      media?: ReactNode;
    }
  | {
      size: 'sm';
      textAction: Action;
      filledAction?: Action;
      media?: ReactNode;
    }
  | ({
      size: 'sm';
      media?: ReactNode;
    } & WithStepper);

type SizeLg =
  | {
      size: 'lg';
      media: ReactNode;
    }
  | {
      size: 'lg';
      media: ReactNode;
      filledAction: Action;
    }
  | ({
      size: 'lg';
      media: ReactNode;
    } & WithStepper);

type SizeProps = SizeSm | SizeLg;

export type InfoModalProps = {
  /**
   * Prefer using string for headline, but ReactNode is allowed for `<Trans />`
   */
  headline: ReactNode;
  /**
   * Prefer using string for description, but ReactNode is allowed for `<Trans />` or things like `<bold>` tags
   */
  description: ReactNode;
  pillText: string;
  open: boolean;
  onClose: () => void;
} & SizeProps;

const DialogStyles = styled(AriaDialog)<{
  $loading?: boolean;
  $size: Size;
}>`
  position: relative;
  background: ${PURPLE_1000};
  width: ${props =>
    props.$size === 'lg' ? props.theme.sizingInfoModalLgWidth : props.theme.sizingInfoModalSmWidth};
  max-width: 95vw;
  border: 0;
  border-radius: ${props =>
    props.$size === 'lg' ? props.theme.radiusInfoModalLg : props.theme.radiusInfoModalSm};
  display: grid;

  grid-template-columns: ${props =>
    props.$size === 'lg' && '[body] min(100%, 27.5rem) [media] 1fr'};
  grid-template-rows: ${props => props.$size !== 'lg' && '[media] auto [body] auto'};
  overflow: hidden;
`;

const CloseButtonPosition = styled.div`
  position: absolute;
  top: ${SPACING_3};
  right: ${SPACING_3};
  line-height: 0;
`;

const Texts = styled.div<{ $size: Size }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_0_5};
  h4 {
    color: ${({ theme }) => theme.colorTextHeadlineOnDark};
    text-transform: uppercase;
    font-weight: 900;
    line-height: 100%;
    letter-spacing: -0.015rem;
  }
  p {
    color: ${({ theme }) => theme.colorTextBodyOnDark};
  }
${({ $size }) =>
  $size === 'lg' &&
  css`
    h4 {
      ${textStyles.headlineLarge}
      letter-spacing: -0.02rem;
      font-weight: 900;
    }
    gap: ${DESKTOP_INFO_MODAL_VERTICAL_GAP};
`}
`;

const TopRow = styled.div`
  padding-right: ${SPACING_6};
  height: ${SIZING_3};
  display: flex;
  align-items: center;
`;

const BottomRow = styled.footer<{ $size: Size; $singleAction: boolean }>`
${({ $singleAction }) =>
  !$singleAction &&
  css`
    display: flex;
    justify-content: space-between;
    gap: ${SPACING_0_5};
    align-items: center;
  `}
`;

const Buttons = styled.div<{ $size: Size; $singleAction: boolean }>`
${({ $singleAction }) =>
  $singleAction
    ? css`
    display: grid;
    `
    : css`
      display: flex;
      flex-wrap: wrap;
      gap: ${SPACING_3};
      align-items: center;
  `}
`;

const MediaWrapper = styled.div<{ $size: Size }>`
  grid-area: media;
  aspect-ratio: ${props => (props.$size === 'lg' ? '1 / 1' : '16 / 9')};
  height: ${props => props.$size === 'lg' && '100%'};
  position: relative;
  background: ${GREY_300};
  overflow: hidden;
  border-radius: ${({ theme, $size }) =>
    $size === 'lg'
      ? `0 ${theme.radiusInfoModalLg} ${theme.radiusInfoModalLg} 0`
      : `${theme.radiusInfoModalSm} ${theme.radiusInfoModalSm} 0 0`};
  ${media.lessThan('phablet')} {
    display: ${props => props.$size === 'lg' && 'none'};
  }
  > * {
    position: absolute;
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

const DialogBody = styled.div<{ $size: Size }>`
  grid-area: body;
  padding: ${props =>
    props.$size === 'lg'
      ? `${DESKTOP_INFO_MODAL_VERTICAL_PADDING_LG} ${DESKTOP_INFO_MODAL_HORIZONTAL_PADDING_LG}`
      : `${DESKTOP_INFO_MODAL_VERTICAL_PADDING_SM} ${DESKTOP_INFO_MODAL_HORIZONTAL_PADDING_SM}`};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${DESKTOP_INFO_MODAL_VERTICAL_GAP};
  ${media.lessThan('phablet')} {
    gap: ${MOBILE_INFO_MODAL_VERTICAL_GAP};
    padding: ${props =>
      props.$size === 'lg'
        ? `${MOBILE_INFO_MODAL_VERTICAL_PADDING_LG} ${MOBILE_INFO_MODAL_HORIZONTAL_PADDING_LG}`
        : `${MOBILE_INFO_MODAL_VERTICAL_PADDING_SM} ${MOBILE_INFO_MODAL_HORIZONTAL_PADDING_SM}`};
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DESKTOP_INFO_MODAL_VERTICAL_GAP};
  ${media.lessThan('phablet')} {
    gap: ${MOBILE_INFO_MODAL_VERTICAL_GAP};
  }
`;

export const InfoModal = ({
  headline,
  description,
  pillText,
  media,
  open,
  onClose,
  size,
  ...props
}: InfoModalProps) => {
  const textAction = 'textAction' in props ? props.textAction : undefined;
  const filledAction = 'filledAction' in props ? props.filledAction : undefined;

  const { t } = useTranslation();
  const descriptionId = useId();
  const headlineId = useId();

  const singleAction = size === 'lg' && filledAction;

  return (
    <UiLibModal open={open}>
      <DialogStyles $size={size} aria-labelledby={headline ? headlineId : descriptionId}>
        {media ? <MediaWrapper $size={size}>{media}</MediaWrapper> : null}
        <DialogBody $size={size}>
          <div />
          <MainContent>
            <TopRow>
              <Badge variant="commercial" text={pillText} onDark />
            </TopRow>
            <CloseButtonPosition>
              {media ? (
                <UiLibCloseButton onClick={onClose} />
              ) : (
                <Button
                  onDark
                  configuration="text"
                  inline
                  type="button"
                  onClick={onClose}
                  icon={<Icons.Close />}
                  aria-label={t('common_close')}
                />
              )}
            </CloseButtonPosition>
            <Texts $size={size}>
              <Text as="h4" variant="headlineMedium" id={headlineId}>
                {headline}
              </Text>

              <Text as="p" variant="bodySmall" id={descriptionId}>
                {description}
              </Text>
            </Texts>
          </MainContent>
          <BottomRow $size={size} $singleAction={!!singleAction}>
            {'steps' in props ? (
              <Stepper activeIndex={props.activeIndex} steps={props.steps} type="mini" />
            ) : (
              <div />
            )}
            <Buttons $size={size} $singleAction={!!singleAction}>
              {'steps' in props ? (
                <>
                  <Button onDark configuration="text" type="button" onClick={onClose}>
                    {t('common_skip')}
                  </Button>
                  {props.activeIndex >= props.steps.length - 1 ? (
                    <Button
                      onDark
                      configuration="commercial"
                      {...(typeof props.confirmAction.href === 'string'
                        ? {
                            href: props.confirmAction.href,
                          }
                        : {
                            type: 'button',
                            onClick: props.confirmAction.onClick,
                          })}
                      disabled={props.confirmAction.disabled}
                      loading={props.confirmAction.loading}
                    >
                      {props.confirmAction.text}
                    </Button>
                  ) : (
                    <Button
                      onDark
                      configuration="commercial"
                      type="button"
                      onClick={() => {
                        props.steps[props.activeIndex + 1]?.onClick();
                      }}
                    >
                      {t('common_next')}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {textAction ? (
                    <Button
                      onDark
                      configuration="text"
                      {...(typeof textAction.href === 'string'
                        ? {
                            href: textAction.href,
                            newTab: textAction.newTab,
                            onClick: textAction.onClick,
                          }
                        : {
                            type: 'button',
                            onClick: textAction.onClick,
                          })}
                      disabled={textAction.disabled}
                      loading={textAction.loading}
                    >
                      {textAction.text}
                    </Button>
                  ) : null}
                  {filledAction ? (
                    <Button
                      onDark
                      configuration="commercial"
                      trailingIcon={filledAction?.trailingIcon}
                      {...(typeof filledAction.href === 'string'
                        ? {
                            href: filledAction.href,
                            onClick: filledAction.onClick,
                          }
                        : {
                            type: 'button',
                            onClick: filledAction.onClick,
                          })}
                      disabled={filledAction.disabled}
                      loading={filledAction.loading}
                    >
                      {filledAction.text}
                    </Button>
                  ) : null}
                </>
              )}
            </Buttons>
          </BottomRow>
        </DialogBody>
      </DialogStyles>
    </UiLibModal>
  );
};
